import { useToast } from '@chakra-ui/react';
import { FeatureType, NestedHome } from '@habity/types';
import { useUser } from '~/hooks/useUser';
import { useHomes } from '~/hooks/useHomes';
import { isBrokerOrAdmin } from '~/lib/roles';
import { isHomeOwner, isSellerClaimed } from '@habity/services';
import { IconType } from 'react-icons';
import { FeatureListEditor } from '~/components/ui/FeatureListEditor';

export type TableDataItem = {
  icon?: IconType;
  label?: string;
  value: string | number;
  comment?: string;
};

interface FeaturesListProps {
  home: NestedHome;
}

export const FeaturesList = ({ home }: FeaturesListProps) => {
  const { user } = useUser();
  const { updateHome } = useHomes();
  const toast = useToast();

  // Allow editing for brokers/admins, or home owners on seller_claimed homes
  const canOwnerEdit = user && isHomeOwner(home, user) && isSellerClaimed(home);
  const isUserAllowedToEdit = isBrokerOrAdmin(user) || canOwnerEdit;

  const handleSaveFeatures = async (newFeatures: FeatureType[]) => {
    if (!home?.id) return;

    try {
      await updateHome(home.id, {
        features: newFeatures,
      });
    } catch (error) {
      console.error('Failed to update features:', error);
      toast({
        title: 'Något gick fel',
        description: 'Kunde inte spara fördelar',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error; // Re-throw to let the FeatureListEditor handle the error state
    }
  };

  // Only show for brokers/admins, or home owners on seller_claimed homes
  if (!isUserAllowedToEdit) return null;

  const showFeaturesPublicly = home.status?.show_features === true;
  const title = showFeaturesPublicly
    ? 'Fördelar med bostaden'
    : 'Fördelar med bostaden (visas ej publikt)';

  return (
    <FeatureListEditor
      title={title}
      features={home?.features || []}
      onSave={handleSaveFeatures}
      canEdit={isUserAllowedToEdit}
      benefits={home?.benefits || []}
    />
  );
};
